/**
 * API Fetch Wrapper with JWT Token Refresh
 *
 * Wraps all API calls to check if the JWT token is older than 45 minutes.
 * If so, calls POST /auth/refresh to get a new token before making the API call.
 * Updates localStorage with the new token.
 * If refresh fails (token expired), redirects to /auth.
 */

import { BACKEND_URL } from '../config';

const TOKEN_REFRESH_THRESHOLD_MS = 45 * 60 * 1000; // 45 minutes
const TOKEN_KEY = 'voxvault_token';
const TOKEN_TIMESTAMP_KEY = 'voxvault_token_timestamp';

/**
 * Get the stored token timestamp (when the token was last set/refreshed).
 * Returns 0 if no timestamp is stored.
 */
function getTokenTimestamp() {
  const ts = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
  return ts ? parseInt(ts, 10) : 0;
}

/**
 * Check if the token is older than the refresh threshold.
 */
function isTokenStale() {
  const tokenTime = getTokenTimestamp();
  if (!tokenTime) return true; // No timestamp means we should refresh
  return Date.now() - tokenTime > TOKEN_REFRESH_THRESHOLD_MS;
}

/**
 * Attempt to refresh the JWT token via POST /auth/refresh.
 * Returns the new token on success, or null on failure.
 */
async function refreshAuthToken() {
  const currentToken = localStorage.getItem(TOKEN_KEY);
  if (!currentToken) return null;

  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      const newToken = data.access_token || data.token;
      if (newToken) {
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(TOKEN_TIMESTAMP_KEY, String(Date.now()));
        return newToken;
      }
    }
    // Refresh failed — token is likely expired
    handleTokenExpired();
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    handleTokenExpired();
    return null;
  }
}

/**
 * Handle expired token by clearing storage and redirecting to /auth.
 */
function handleTokenExpired() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
  // Redirect to auth page if we're in a browser environment
  if (typeof window !== 'undefined' && window.location.pathname !== '/auth') {
    window.location.href = '/auth';
  }
}

/**
 * Get the current valid token, refreshing if necessary.
 * Returns the token string or null if no token / refresh failed.
 */
async function getValidToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return null;
  }

  if (isTokenStale()) {
    const newToken = await refreshAuthToken();
    return newToken;
  }

  return token;
}

/**
 * apiFetch — a drop-in replacement for fetch that automatically:
 *  - Attaches the Authorization header with the JWT token
 *  - Refreshes the token if it's older than 45 minutes
 *  - Redirects to /auth if the token is expired and can't be refreshed
 *
 * @param {string} url - Full URL to fetch (same as you'd pass to fetch)
 * @param {object} options - Standard fetch options. Authorization header is set automatically.
 * @returns {Promise<Response>} - The fetch Response object
 */
export async function apiFetch(url, options = {}) {
  const token = await getValidToken();

  if (!token) {
    // No token available — redirect to auth
    handleTokenExpired();
    // Return a rejected promise so callers can handle gracefully
    throw new Error('No valid authentication token');
  }

  // Merge headers — caller-provided headers take precedence except for Authorization
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  return fetch(url, { ...options, headers });
}

/**
 * Convenience helper to get the current valid token (refreshing if needed).
 * Useful for cases where you need the token directly (e.g., audio src query params).
 */
export { getValidToken };

/**
 * Set the token and its timestamp in localStorage.
 * Called after login to record when the token was issued.
 */
export function setAuthToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_TIMESTAMP_KEY, String(Date.now()));
}

/**
 * Clear the token and timestamp from localStorage.
 */
export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
}